import {
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { LanguageServiceService } from '../../../../services/language-service.service';



type BlogCategoryId = 'all' | 'career' | 'cv' | 'travel' | 'interview';

interface BlogCategory {
  id: BlogCategoryId;
  labelKey: string;
}

interface BlogPost {
  id: number;
  slug: string;
  iconImg: string;
  category: Exclude<BlogCategoryId, 'all'>;
  categoryKey: string;
  titleKey: string;
  excerptKey: string;
  dateKey: string;
  readTimeKey: string;
  tagsKeys: string[];
  featured?: boolean;
}

@Component({
  selector: 'app-blogs',
  templateUrl: './blogs.component.html',
  styleUrls: ['./blogs.component.scss'],
})
export class BlogsComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private subscriptions = new Subscription();

  currentLanguage = 'en';

  searchTerm = '';
  activeCategory: BlogCategoryId = 'all';

  constructor(
    private languageService: LanguageServiceService,
    private transloco: TranslocoService
  ) {
    if (this.isBrowser) {
      this.currentLanguage = localStorage.getItem('language') || 'en';
    }

    this.transloco.setActiveLang(this.currentLanguage);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.languageService.currentLanguage$.subscribe((lang) => {
        this.currentLanguage = lang;
        this.transloco.setActiveLang(lang);

        if (this.isBrowser) {
          localStorage.setItem('language', lang);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isAr(): boolean {
    return this.currentLanguage === 'ar';
  }

  categories: BlogCategory[] = [
    { id: 'all', labelKey: 'atw.blogs.categories.all' },
    { id: 'career', labelKey: 'atw.blogs.categories.career' },
    { id: 'cv', labelKey: 'atw.blogs.categories.cv' },
    { id: 'travel', labelKey: 'atw.blogs.categories.travel' },
    { id: 'interview', labelKey: 'atw.blogs.categories.interview' },
  ];

blogs: BlogPost[] = [
  {
    id: 1,
    slug: 'how-to-build-a-strong-cv',
    iconImg: '/assets/as/30.webp',
    category: 'cv',
    categoryKey: 'atw.blogs.posts.post1.category',
    titleKey: 'atw.blogs.posts.post1.title',
    excerptKey: 'atw.blogs.posts.post1.excerpt',
    dateKey: 'atw.blogs.posts.post1.date',
    readTimeKey: 'atw.blogs.posts.post1.readTime',
    tagsKeys: [
      'atw.blogs.tags.cv',
      'atw.blogs.tags.keywords',
      'atw.blogs.tags.jobs',
    ],
    featured: true,
  },
  {
    id: 2,
    slug: 'prepare-for-your-first-interview',
    iconImg: '/assets/as/17.webp',
    category: 'interview',
    categoryKey: 'atw.blogs.posts.post2.category',
    titleKey: 'atw.blogs.posts.post2.title',
    excerptKey: 'atw.blogs.posts.post2.excerpt',
    dateKey: 'atw.blogs.posts.post2.date',
    readTimeKey: 'atw.blogs.posts.post2.readTime',
    tagsKeys: [
      'atw.blogs.tags.interview',
      'atw.blogs.tags.confidence',
      'atw.blogs.tags.tips',
    ],
  },
  {
    id: 3,
    slug: 'top-career-mistakes-to-avoid',
    iconImg: '/assets/as/39.webp',
    category: 'career',
    categoryKey: 'atw.blogs.posts.post3.category',
    titleKey: 'atw.blogs.posts.post3.title',
    excerptKey: 'atw.blogs.posts.post3.excerpt',
    dateKey: 'atw.blogs.posts.post3.date',
    readTimeKey: 'atw.blogs.posts.post3.readTime',
    tagsKeys: [
      'atw.blogs.tags.career',
      'atw.blogs.tags.growth',
      'atw.blogs.tags.planning',
    ],
  },
  {
    id: 4,
    slug: 'working-abroad-checklist',
    iconImg: '/assets/as/1.webp',
    category: 'travel',
    categoryKey: 'atw.blogs.posts.post4.category',
    titleKey: 'atw.blogs.posts.post4.title',
    excerptKey: 'atw.blogs.posts.post4.excerpt',
    dateKey: 'atw.blogs.posts.post4.date',
    readTimeKey: 'atw.blogs.posts.post4.readTime',
    tagsKeys: [
      'atw.blogs.tags.travel',
      'atw.blogs.tags.documents',
      'atw.blogs.tags.visa',
    ],
  },
  {
    id: 5,
    slug: 'how-to-match-your-skills-with-jobs',
    iconImg: '/assets/as/15.webp',
    category: 'career',
    categoryKey: 'atw.blogs.posts.post5.category',
    titleKey: 'atw.blogs.posts.post5.title',
    excerptKey: 'atw.blogs.posts.post5.excerpt',
    dateKey: 'atw.blogs.posts.post5.date',
    readTimeKey: 'atw.blogs.posts.post5.readTime',
    tagsKeys: [
      'atw.blogs.tags.skills',
      'atw.blogs.tags.matching',
      'atw.blogs.tags.jobs',
    ],
  },
  {
    id: 6,
    slug: 'cv-keywords-that-help-you-get-noticed',
    iconImg: '/assets/as/2.webp',
    category: 'cv',
    categoryKey: 'atw.blogs.posts.post6.category',
    titleKey: 'atw.blogs.posts.post6.title',
    excerptKey: 'atw.blogs.posts.post6.excerpt',
    dateKey: 'atw.blogs.posts.post6.date',
    readTimeKey: 'atw.blogs.posts.post6.readTime',
    tagsKeys: [
      'atw.blogs.tags.cv',
      'atw.blogs.tags.keywords',
      'atw.blogs.tags.ats',
    ],
  },
];

  get featuredBlog(): BlogPost | undefined {
    return this.blogs.find((blog) => blog.featured);
  }

  get filteredBlogs(): BlogPost[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.blogs.filter((blog) => {
      const matchesCategory =
        this.activeCategory === 'all' || blog.category === this.activeCategory;

      const searchableText = [
        blog.slug,
        blog.category,
        blog.titleKey,
        blog.excerptKey,
        ...blog.tagsKeys,
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !term || searchableText.includes(term);

      return matchesCategory && matchesSearch;
    });
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  setCategory(category: BlogCategoryId): void {
    this.activeCategory = category;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.activeCategory = 'all';
  }

  trackByBlog(index: number, blog: BlogPost): number {
    return blog.id;
  }

  trackByCategory(index: number, category: BlogCategory): string {
    return category.id;
  }
}